import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { requestReset } from "@/lib/auth-client";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { track } from "@/lib/track";
import { useT } from "@/i18n";

type Mode = "sign-in" | "sign-up" | "forgot";

export interface AuthFormProps {
  defaultMode?: Mode;
  /** Compact = used in the modal; large = used on the standalone page. */
  size?: "compact" | "large";
}

export function AuthForm({ defaultMode = "sign-in", size = "large" }: AuthFormProps) {
  const t = useT();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === "sign-in") {
        await signIn(email, password);
        track("auth_result", { action: "sign_in", method: "password", outcome: "success" });
      } else if (mode === "sign-up") {
        await signUp(email, password, handle);
        track("auth_result", { action: "sign_up", method: "password", outcome: "success" });
      } else {
        await requestReset(email);
        track("password_reset_request", { outcome: "success" });
        setInfo("If that email exists, a reset link is on its way. Check your inbox (and the dev logs in development).");
      }
    } catch (err) {
      if (mode === "forgot") {
        track("password_reset_request", { outcome: "failure" });
      } else {
        track("auth_result", {
          action: mode === "sign-in" ? "sign_in" : "sign_up",
          method: "password",
          outcome: "failure",
        });
      }
      setError(humanize(err));
    } finally {
      setBusy(false);
    }
  }

  const heading =
    mode === "sign-in"
      ? "Welcome back."
      : mode === "sign-up"
        ? "Join Kitdex."
        : "Reset your password";

  const sub =
    mode === "forgot"
      ? "Enter your email and we'll send you a reset link."
      : "Sign in to scan and identify.";

  return (
    <div className={size === "compact" ? "" : "p-6 md:p-8"}>
      {size === "large" && (
        <div className="border-b border-foreground -mx-6 md:-mx-8 px-6 md:px-8 pb-4 mb-6 flex">
          <ModeTab active={mode === "sign-in"} onClick={() => { setMode("sign-in"); setError(null); setInfo(null); }}>Sign in</ModeTab>
          <ModeTab active={mode === "sign-up"} onClick={() => { setMode("sign-up"); setError(null); setInfo(null); }}>Create account</ModeTab>
        </div>
      )}

      <h1 className={size === "large" ? "font-serif text-3xl font-black leading-tight" : "font-serif text-2xl font-black leading-tight"}>
        {heading}
      </h1>
      {mode === "forgot" && <p className="mt-2 text-sm text-muted-foreground">{sub}</p>}

      {mode !== "forgot" && (
        <div className="mt-5">
          <GoogleSignInButton />
        </div>
      )}

      <form onSubmit={onSubmit} className={mode === "forgot" ? "mt-5 space-y-3" : "space-y-3"}>
        {mode === "sign-up" && (
          <Field
            label="Collector handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="e.g. kappa-fan-99"
            autoComplete="username"
            required
            minLength={2}
            maxLength={32}
          />
        )}
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete={mode === "sign-up" ? "email" : "username"}
          required
        />
        {mode !== "forgot" && (
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
            minLength={mode === "sign-up" ? 12 : 1}
            maxLength={200}
            required
          />
        )}

        {mode === "sign-up" && (
          <p className="text-xs text-muted-foreground">{t("security.passwordHint")}</p>
        )}
        {error && (
          <p className="font-mono text-xs text-red-700 border border-red-700/40 bg-red-700/5 px-3 py-2">
            {error}
          </p>
        )}
        {info && (
          <p className="font-mono text-xs text-foreground border border-foreground/40 bg-foreground/5 px-3 py-2">
            {info}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] transition-colors disabled:opacity-60"
        >
          {busy
            ? "Working…"
            : mode === "sign-in"
              ? "Sign in"
              : mode === "sign-up"
                ? "Create account"
                : "Send reset link"}
        </button>
      </form>

      <div className="mt-4 flex items-center justify-between text-xs">
        {mode === "sign-in" && (
          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(null); setInfo(null); }}
            className="text-muted-foreground hover:text-foreground underline"
          >
            Forgot password?
          </button>
        )}
        {mode === "forgot" && (
          <button
            type="button"
            onClick={() => { setMode("sign-in"); setError(null); setInfo(null); }}
            className="text-muted-foreground hover:text-foreground underline"
          >
            Back to sign in
          </button>
        )}
        {size === "compact" && mode !== "forgot" && (
          <button
            type="button"
            onClick={() => { setMode(mode === "sign-in" ? "sign-up" : "sign-in"); setError(null); setInfo(null); }}
            className="ml-auto text-muted-foreground hover:text-foreground underline"
          >
            {mode === "sign-in" ? "Create account" : "Have an account? Sign in"}
          </button>
        )}
      </div>
    </div>
  );
}

function ModeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 font-mono text-xs uppercase tracking-[0.2em] py-2 ${active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"} ${active ? "" : "ml-2"}`}
    >
      {children}
    </button>
  );
}

function Field({ label, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <input
        {...props}
        className="mt-1 w-full border border-foreground bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foreground"
      />
    </label>
  );
}

function PasswordField(props: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "label">) {
  const [show, setShow] = useState(false);
  return (
    <div className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Password</span>
      <div className="relative mt-1">
        <input
          {...props}
          type={show ? "text" : "password"}
          className="w-full border border-foreground bg-background px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-foreground"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-0 top-0 h-full px-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function humanize(err: unknown): string {
  if (!err) return "Something went wrong.";
  if (typeof err === "object" && err && "data" in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  if (err instanceof Error && err.message) {
    // Strip "HTTP 401 Unauthorized: " prefix from ApiError messages.
    return err.message.replace(/^HTTP \d{3}[^:]*:\s*/, "");
  }
  return "Something went wrong.";
}
